/**
 * ParticleManager — visual effects using Phaser 3 particles.
 *
 * All textures are generated at runtime (no asset files needed).
 * Effects: tile scatter, combo milestone burst, pattern confetti,
 * level complete fireworks, game over shatter.
 */

import { FONT_SIZE, fontStyle } from "./FontScale";

const PARTICLE_KEY = "particle_square";
const PARTICLE_SIZE = 4;

const ParticleManager = {
  /**
   * Initialize particle textures in the scene.
   * Call once during scene.create().
   * @param {Phaser.Scene} scene
   */
  init(scene) {
    this.scene = scene;
    this.emitters = [];

    // Generate a simple square texture if not already created
    if (!scene.textures.exists(PARTICLE_KEY)) {
      const gfx = scene.make.graphics({ add: false });
      gfx.fillStyle(0xffffff, 1);
      gfx.fillRect(0, 0, PARTICLE_SIZE, PARTICLE_SIZE);
      gfx.generateTexture(PARTICLE_KEY, PARTICLE_SIZE, PARTICLE_SIZE);
      gfx.destroy();
    }
  },

  /**
   * Emit particles when a tile is placed.
   * 3-5 small squares scatter from the placement point.
   * @param {number} x - Pixel x position
   * @param {number} y - Pixel y position
   * @param {number} color - Tint color (hex)
   * @param {number} clusterSize - Size of tile cluster
   */
  emitTilePlacement(x, y, color, clusterSize) {
    if (!this.scene || !this.scene.scene.isActive()) return;

    const centerX = x + clusterSize / 2;
    const centerY = y + clusterSize / 2;
    const count = 3 + Math.floor(Math.random() * 3); // 3-5

    const emitter = this.scene.add.particles(centerX, centerY, PARTICLE_KEY, {
      speed: { min: 30, max: 80 },
      angle: { min: 0, max: 360 },
      scale: { start: 0.8, end: 0 },
      lifespan: 300,
      tint: color,
      quantity: count,
      emitting: false,
    });

    emitter.explode(count);
    this._scheduleCleanup(emitter, 400);
  },

  /**
   * Emit a ring burst for combo milestones (10, 25, 50, 100, 200).
   * @param {number} milestone - The milestone reached
   * @param {number} color - Tier color
   */
  emitComboMilestone(milestone, color) {
    if (!this.scene || !this.scene.scene.isActive()) return;

    const centerX = 400;
    const centerY = 300;
    const count = Math.min(40, 10 + milestone / 5);

    const emitter = this.scene.add.particles(centerX, centerY, PARTICLE_KEY, {
      speed: { min: 100, max: 250 },
      angle: { min: 0, max: 360 },
      scale: { start: 1.2, end: 0 },
      lifespan: 600,
      tint: color,
      quantity: Math.round(count),
      emitting: false,
    });

    emitter.explode(Math.round(count));
    this._scheduleCleanup(emitter, 700);
  },

  /**
   * Emit confetti for pattern chain completion.
   * Multi-colored particles shower from the top.
   * @param {number} x - Center x
   * @param {number} y - Center y
   */
  emitPatternComplete(x, y) {
    if (!this.scene || !this.scene.scene.isActive()) return;

    const colors = [0xff00ff, 0x00ffff, 0xffd700, 0x39ff14, 0xff6600];

    const emitter = this.scene.add.particles(x, y, PARTICLE_KEY, {
      speed: { min: 50, max: 200 },
      angle: { min: 200, max: 340 },
      scale: { start: 1.0, end: 0.2 },
      lifespan: 800,
      tint: colors,
      gravityY: 150,
      quantity: 50,
      emitting: false,
    });

    emitter.explode(50);
    this._scheduleCleanup(emitter, 1000);
  },

  /**
   * Emit fireworks for level completion.
   * Three sequential bursts with staggered timing.
   */
  emitLevelComplete() {
    if (!this.scene || !this.scene.scene.isActive()) return;

    const positions = [
      { x: 200, y: 200 },
      { x: 400, y: 150 },
      { x: 600, y: 200 },
    ];

    const colors = [
      [0xffd700, 0xff6600],
      [0x00ffff, 0x39ff14],
      [0xff00ff, 0x9945ff],
    ];

    positions.forEach((pos, i) => {
      this.scene.time.delayedCall(i * 200, () => {
        if (!this.scene || !this.scene.scene.isActive()) return;

        const emitter = this.scene.add.particles(pos.x, pos.y, PARTICLE_KEY, {
          speed: { min: 80, max: 250 },
          angle: { min: 0, max: 360 },
          scale: { start: 1.5, end: 0 },
          lifespan: 1000,
          tint: colors[i],
          quantity: 60,
          emitting: false,
        });

        emitter.explode(60);
        this._scheduleCleanup(emitter, 1200);
      });
    });
  },

  /**
   * Emit shatter effect for game over (momentum bar breaking apart).
   * @param {number} barX - Left edge of momentum bar
   * @param {number} barY - Top edge of momentum bar
   * @param {number} barWidth - Width of bar
   */
  emitGameOver(barX, barY, barWidth) {
    if (!this.scene || !this.scene.scene.isActive()) return;

    const segmentCount = 20;
    const segWidth = barWidth / segmentCount;

    for (let i = 0; i < segmentCount; i++) {
      const sx = barX + i * segWidth + segWidth / 2;
      const emitter = this.scene.add.particles(sx, barY, PARTICLE_KEY, {
        speed: { min: 50, max: 200 },
        angle: { min: 180, max: 360 },
        scale: { start: 1.0, end: 0 },
        lifespan: 800,
        tint:
          i < 5 ? 0xff0000 : i < 10 ? 0xff6600 : i < 15 ? 0xffd700 : 0x39ff14,
        gravityY: 200,
        quantity: 3,
        emitting: false,
      });

      this.scene.time.delayedCall(i * 30, () => {
        if (emitter && this.scene && this.scene.scene.isActive()) {
          emitter.explode(3);
        }
      });

      this._scheduleCleanup(emitter, 1500);
    }
  },

  /**
   * Emit floating score pop text.
   * @param {number} x - Position x
   * @param {number} y - Position y
   * @param {string} text - Score text (e.g., "+15")
   * @param {string} color - CSS color string
   */
  emitScorePop(x, y, text, color) {
    if (!this.scene || !this.scene.scene.isActive()) return;

    const pop = this.scene.add
      .text(x, y, text, fontStyle(FONT_SIZE.CAPTION, color))
      .setOrigin(0.5)
      .setDepth(100);

    this.scene.tweens.add({
      targets: pop,
      y: y - 40,
      alpha: 0,
      duration: 600,
      ease: "Power2",
      onComplete: () => pop.destroy(),
    });
  },

  /**
   * Schedule cleanup of a particle emitter.
   * @param {Phaser.GameObjects.Particles.ParticleEmitter} emitter
   * @param {number} delay - ms before destroying
   */
  _scheduleCleanup(emitter, delay) {
    if (!this.scene) return;
    this.emitters.push(emitter);

    this.scene.time.delayedCall(delay, () => {
      if (emitter) {
        emitter.destroy();
        const idx = this.emitters.indexOf(emitter);
        if (idx !== -1) this.emitters.splice(idx, 1);
      }
    });
  },

  /**
   * Destroy all active emitters. Call on scene shutdown.
   */
  destroy() {
    this.emitters.forEach((e) => {
      if (e) e.destroy();
    });
    this.emitters = [];
    this.scene = null;
  },
};

export default ParticleManager;
